import * as React from 'react';

import './DestinationStep.scss';

export type DestinationStep = 'company' | 'engagement' | 'request' | 'push';

/** The rail's step labels, already resolved from CommonText by the container. */
export type DestinationStepLabels = {
    client: string;
    engagement: string;
    request: string;
    send: string;
};

type DestinationStepperProps = {
    activeStep: DestinationStep;
    labels: DestinationStepLabels;
    companyName?: string;
    engagementName?: string;
    requestSummary?: string;
};

type StepRow = {
    step: DestinationStep;
    label: string;
    // A selection line sits under this step; it shows the value once the step is done and is reserved before that.
    hasValue: boolean;
    value?: string;
};

export const DestinationStepper: React.FC<DestinationStepperProps> = (props): React.ReactElement => {
    const { activeStep, labels, companyName, engagementName, requestSummary } = props;

    const steps: StepRow[] = [
        { step: 'company', label: labels.client, hasValue: true, value: companyName },
        { step: 'engagement', label: labels.engagement, hasValue: true, value: engagementName },
        { step: 'request', label: labels.request, hasValue: true, value: requestSummary },
        { step: 'push', label: labels.send, hasValue: false },
    ];
    const activeIndex = steps.findIndex(s => s.step === activeStep);

    return (
        <ol className={'page-step-sidebar__list'}>
            {steps.map((s, index) => {
                const status = index < activeIndex ? 'done' : index === activeIndex ? 'active' : 'pending';
                const showValue = status === 'done' && !!s.value;

                return (
                    <li
                        key={s.step}
                        className={`page-step-sidebar__item page-step-sidebar__item--${status}`}
                        aria-current={status === 'active' ? 'step' : undefined}
                    >
                        <div className={'page-step-sidebar__indicator'}>
                            <span className={`page-step-sidebar__dot page-step-sidebar__dot--${status}`} />
                            {index < steps.length - 1 && <span className={'page-step-sidebar__connector'} />}
                        </div>
                        <div className={'page-step-sidebar__text'}>
                            <span className={'page-step-sidebar__label'}>{s.label}</span>
                            {showValue && <span className={'page-step-sidebar__value'}>{s.value}</span>}
                            {/* Reserves the value line, as the POC does, so filling it in does not shift the steps below. */}
                            {!showValue && s.hasValue && <span className={'page-step-sidebar__spacer'} aria-hidden={'true'}>{' '}</span>}
                        </div>
                    </li>
                );
            })}
        </ol>
    );
};
